// src/components/Login.js
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../commonLogin.css";
import Toast from "../../utils/toast";
import Loader from "../Loader";
import createInstance from "../../api/axiosinstance";
import { useDispatch, useSelector } from "react-redux";
import {
  loginSuccess,
  setLoading,
  setError,
  logout,
  loginAdmin,
  loginOrgUser,
  checkUserType,
  requestCandidateOtp,
  getAllTPInterviewers,
  loginTPUser,
} from "../../Redux/slices/authSlice";
import {
  Button,
  FormControl,
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Typography,
  Paper,
  Avatar,
  Divider,
  Fade,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
} from "@mui/material";

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    backgroundColor: "#ffffff",

    "& fieldset": {
      borderColor: "rgb(37, 99, 235)",
    },

    "&:hover fieldset": {
      borderColor: "rgb(37, 99, 235)",
    },

    "&.Mui-focused fieldset": {
      borderColor: "rgb(37, 99, 235)",
      borderWidth: 2,
    },
  },

  "& input": {
    color: "#111827",
    padding: "14px",

    "&::placeholder": {
      color: "black",
      opacity: 1, 
      fontWeight: 500,
    },

    "&::-webkit-input-placeholder": {
      color: "black",
      opacity: 1,
    },

    "&::-moz-placeholder": {
      color: "rgb(37, 99, 235)",
      opacity: 1,
    },

    "&:-ms-input-placeholder": {
      color: "rgb(37, 99, 235)",
    },
  },

  "& .MuiInputAdornment-root i": {
    color: "rgb(37, 99, 235)",
  },

  "& .MuiFormHelperText-root": {
    color: "#dc2626",
    fontSize: "12px",
  },
};

const Login = () => {
  const dispatch = useDispatch();
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const { isAuthenticated, loading, userType, user } = useSelector(
    (state) => state.auth,
  );
  
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    role: "",
    organization: "",
  });
  const [apiError, setApiError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const dishaApi = createInstance("DISHA");
  const [step, setStep] = useState("email");

  useEffect(() => {
    if (isAuthenticated && user) {
      const route = user.role?.toLowerCase() + "Landing";
      navigate("/" + route);
    }
  }, [isAuthenticated, user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setApiError(null);
  };

  const validateEmail = () => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      setErrors({ ...errors, email: "Please enter a valid email address" });
      return false;
    }
    return true;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail()) return;

    const safeEmail = formData.email.trim().toLowerCase();

    dispatch(setLoading(true));
    setApiError(null);
    
    try {
      const result = await dispatch(checkUserType(safeEmail)).unwrap();
      if (result.role === "candidate") {
        await dispatch(requestCandidateOtp(safeEmail)).unwrap();
        navigate("/otpVerification", {
          state: { email: safeEmail, source: "candidateLogin" },
        });
        Toast.success("OTP sent successfully");
      } else {
        setStep("password");
        //Toast.info("Please enter your password");
      }
    } catch (err) {
      // Safely extract the string message if 'err' is an object
      const errorMsg = typeof err === "string" ? err : (err?.message || "User not found");
      Toast.error(errorMsg);
      setApiError(errorMsg);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Silently trim the spaces before validation and submission
    const passwordValue = formData.password.trim();
    
    setApiError(null);
    
    if (!passwordValue) {
      setErrors((prev) => ({ ...prev, password: "Please enter a password" }));
      return;
    }

    const safeEmail = formData.email.trim().toLowerCase();

    dispatch(setLoading(true));
    try {
      let userData;

      if (userType === "Admin") {
        userData = await dispatch(
          loginAdmin({ email: safeEmail, password: passwordValue }),
        ).unwrap();
      } else if (["OrgAdmin", "Interviewer", "HR"].includes(userType)) {
        userData = await dispatch(
          loginOrgUser({ email: safeEmail, password: passwordValue }),
        ).unwrap();
      } else if (userType === "TP-Interviewer") {
        const allInterviewers = await dispatch(getAllTPInterviewers()).unwrap();
        const matchedUser = allInterviewers.find(
          (u) => u.email === safeEmail,
        );

        if (matchedUser && matchedUser.activation_status === "Pending") {
          setShowVerificationPopup(true);
          dispatch(setLoading(false));
          return;
        }

        userData = await dispatch(
          loginTPUser({ email: safeEmail, password: passwordValue }),
        ).unwrap();
      }

      if (!userData || userData.error || (userData.message && userData.message.toLowerCase().includes("invalid")) || !userData.role) {
        throw new Error("Invalid credentials");
      }

      if (userData?.activation_status === "Pending") {
        setShowVerificationPopup(true);
        dispatch(setLoading(false));
        return;
      }

      if (userData?.status === "Active") {
        navigate(`/${userData.role.toLowerCase()}Landing`);
      } else if (userData?.status === "Inactive") {
        navigate("/changePassword");
      } else {
        navigate(`/${userData.role.toLowerCase()}Landing`); 
      }

      Toast.success("Logged in successfully");
    } catch (err) {
      Toast.error("Invalid username or password");
      setApiError("Login failed"); 
    } finally {
      dispatch(setLoading(false));
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const goBack = () => {
    setStep("email");
    setErrors({ ...errors, password: "" });
    setApiError(null);
  };

  return (
    <Fade in={true} timeout={800}>
      <Box>
        <Box sx={{ width: "100%", maxWidth: 420, padding: "20px" }}>
          <Box
            sx={{
              textAlign: "center",
              mb: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mb: 2,
                background: "#334155",
              }}
            >
              <i className="bi bi-person-circle" style={{ fontSize: 28 }}></i>
            </Avatar>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: "#334155",
                letterSpacing: "0.4px",
              }}
            >
              {step === "email" ? "Sign In" : "Enter Password"}
            </Typography>

            <Typography
              sx={inputStyles}
            >
              {step === "email"
                ? "Enter your email to continue to Strategic Hire"
                : `Welcome back, let's verify it's you`}
            </Typography>
          </Box>

          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
            onSubmit={
              step === "email" ? handleEmailSubmit : handlePasswordSubmit
            }
          >
            {step === "email" ? (
              <TextField
                type="email"
                name="email"
                placeholder="Email Address"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
                fullWidth
                size="medium"
                error={!!errors.email}
                helperText={errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <i
                        className="bi bi-envelope"
                        style={{ color: "rgb(37, 99, 235)" }}
                      ></i>
                    </InputAdornment>
                  ),
                }}
                sx={inputStyles}
              />
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 2,
                    backgroundColor: "rgb(37, 99, 235)",
                    borderRadius: "12px",
                    mb: 2,
                    cursor: "pointer",
                  }}
                  onClick={goBack}
                >
                  <i
                    className="bi bi-envelope"
                    style={{ color: "#000000", marginRight: "10px" }}
                  ></i>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flexGrow: 1,
                    }}
                  >
                    <Typography variant="body1" sx={{ color: "#000000" }}>
                      {formData.email}
                    </Typography>
                  </Box>
                  <i className="bi bi-pencil" style={{ color: "#000000" }}></i>
                </Box>

                <TextField
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  autoFocus
                  fullWidth
                  variant="outlined"
                  size="medium"
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <i
                          className="bi bi-lock"
                          style={{ color: "rgb(37, 99, 235)" }}
                        ></i>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                          sx={{ color: "#00a2a2" }}
                        >
                          {showPassword ? (
                            <i className="bi bi-eye"></i>
                          ) : (
                            <i className="bi bi-eye-slash"></i>
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={inputStyles}
                />
              </>
            )}

            {step === "password" && (
              <Box sx={{ textAlign: "right", mt: -2 }}>
                <Link to="/forgotPassword" className="text-decoration-none">
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgb(37, 99, 235)",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Forgot Password?
                  </Typography>
                </Link>
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                borderRadius: "12px",
                padding: "12px",
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: "600",
                letterSpacing: "0.5px",
                background: "rgb(37, 99, 235)",
                boxShadow: "0 4px 10px rgba(0, 162, 162, 0.3)",
                "&:hover": {
                  background: "#334155",
                  boxShadow: "0 6px 15px rgba(0, 162, 162, 0.4)",
                },
                mt: 2,
                color:"#ffffff"
              }}
            >
              {loading ? (
                <Loader />
              ) : (
                <>
                  {step === "email" ? "Continue" : "Sign In"}
                  <i
                    className="bi bi-arrow-right"
                    style={{ marginLeft: "8px" }}
                  ></i>
                </>
              )}
            </Button>
          </Box>

         {apiError && (
            <Typography
              variant="body2"
              sx={{
                color: "#ff5252",
                mt: 2,
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="bi bi-exclamation-circle"
                style={{ marginRight: "6px" }}
              ></i>
              {/* Force it to render a string just in case */}
              {typeof apiError === "string" ? apiError : "Login failed"}
            </Typography>
          )}

        </Box>
        <Dialog
          open={showVerificationPopup}
          onClose={() => setShowVerificationPopup(false)}
          PaperProps={{
            sx: {
              borderRadius: 4,
              backgroundColor: "white",
              color: "white",
              padding: 2,
              backdropFilter: "blur(10px)",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              fontWeight: 700,
              color: "#334155",
            }}
          >
            Account Under Verification
          </DialogTitle>

          <DialogContent>
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                color: "black",
                mt: 1,
              }}
            >
              Your account is currently under verification. Please come back
              later once it’s approved.
            </Typography>
          </DialogContent>

          <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
            <Button
              onClick={() => {
                setShowVerificationPopup(false);
                navigate("/");
              }}
              sx={{
                borderRadius: "12px",
                px: 4,
                py: 1,
                background: "#1976d2",
                color: "white",
                textTransform: "none",
                "&:hover": {
                  background: "#334155",
                },
              }}
            >
              Home
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default Login;